import { defineStore } from "pinia";
import { useTrazabilidadStore } from "@/stores/trazabilidad";

import dayjs from "dayjs";

import * as ofertasApi from "@/services/ofertas";
import * as Plazos from "@/enums/Plazos";
import * as TipoModulo from "@/enums/TipoModulo";
import * as unidadesNegocioApi from "@/services/unidadesNegocio";

import { crearOfertaDescuentoFirma } from "@/services/ofertaDescuentoFirma";
import { formatDateEs, formatDateAddDaysForm, formatDateSinZona } from "@/services/date";
import * as clientesApi from "@/services/clientes";
import complejidadesApi from "@/services/complejidadesProyecto";
import * as divisasApi from "@/services/divisas";
import * as duracionesContratoApi from "@/services/duracionesContrato";
import * as paisesApi from "@/services/paises";
import formasDePagoApi from "@/services/formasPago";
import planesApi from "@/services/planes";
import tarifasApi from "@/services/tarifas";
import * as TipoCuota from "@/enums/TipoCuota";
import * as tipoDescuentoApi from "@/services/tipoDescuento";
import TipoProyecto from "@/enums/TipoProyecto";
import * as usuariosApi from "@/services/usuarios";

export const useOfertaFormStore = defineStore("ofertaForm", {
  state() {
    return {
      campanias: [],
      cargaInicial: true,
      // Cliente
      cliente: clientesApi.crear(),
      clienteContactos: [],
      clienteContacto: {},
      pais: paisesApi.crear(),
      paises: [],
      complejidades: [],
      // Divisas
      divisas: [], // Al cargar el cliente recibo las divisas
      divisa: divisasApi.crear(),
      // Planes
      planes: [], // Al cargar recibo los planes
      formasDePago: [], // todas las formas de pago
      modificadorPrecio: null,
      modulos: {},
      oferta: ofertasApi.crearOferta(),
      ofertaBase: ofertasApi.crearOfertaBase(),
      ofertaDescuentoFirma: crearOfertaDescuentoFirma(),
      ofertaValoradaEuro: {},
      ofertaModulos: [],
      ofertaPipedrive: {},
      tarifa: tarifasApi.crear(),
      tieneContrato: false,
      duracionesContrato: [],
      duracionContrato: {},
      tiposDescuentoparaCuota: [], // todos los tipos de descuento
      tiposDescuentoParaServicios: [], // todos los tipos de descuento
      ultimaOfertaModificada: null,
      unidadNegocio: {},
      unidadesNegocio: [],
      usuario: usuariosApi.crear(),
      motivoCierre: {},
    };
  },
  getters: {
    borrador() {
      return Boolean(this.oferta.borrador && !this.oferta.caducada);
    },
    puedeAgregarModulos() {
      return this.oferta.divisaId > 0 && this.oferta.campaniaId > 0 && this.oferta.planId > 0;
    },
    puedeValorar() {
      return this.oferta.id && this.oferta.divisaId && this.divisa.id;
    },
    soloModulos() {
      return this.ofertaModulos.filter((x) => this.modulos[x.moduloId].tipo != TipoModulo.SERVICIO);
    },
    soloServicios() {
      return this.ofertaModulos.filter((x) => this.modulos[x.moduloId].tipo == TipoModulo.SERVICIO);
    },
    numModulos() {
      return this.soloModulos.length;
    },
    numServicios() {
      return this.soloServicios.length;
    },
    endpointModulos() {
      const modulos = this.soloModulos.map((m) => m.moduloId).join(",");
      const tipos = [TipoModulo.BASE, TipoModulo.MODULO].join(",");
      return this.buildEndpointParaModulos(modulos, tipos);
    },
    endpointServicios() {
      const modulos = this.soloServicios.map((m) => m.moduloId).join(",");
      const tipos = [TipoModulo.SERVICIO].join(",");
      return this.buildEndpointParaModulos(modulos, tipos);
    },
    esSaas() {
      return this.oferta.tipoProyecto == TipoProyecto.SAAS;
    },
    comboModulosBloqueado() {
      return !(this.oferta.divisaId > 0 && this.oferta.planId > 0 && this.oferta.campaniaId > 0);
    },
    comboFormasPago() {
      return this.formasDePago.map((fp) => {
        return {
          value: fp.id,
          text: fp.nombre,
        };
      });
    },

    nombreFormaPago() {
      let id = null;
      if (this.oferta.tipoProyecto == TipoProyecto.SAAS) {
        id = this.oferta.formaPagoImplementacionId;
      } else {
        id = this.oferta.formaPagoSuscripcionId;
      }
      const fp = this.formasDePago.find((fp) => fp.id == id);
      return fp ? fp.nombre : "N/D";
    },
    maximoNumeroUsuarios() {
      if (!this.plan) {
        return 0;
      }
      return this.plan.maximoNumeroUsuarios;
    },
    opcionesTipoCuota() {
      if (!this.duracionContrato) {
        return [];
      }
      const options = TipoCuota.tiposDeCuota.filter((x) => x.meses <= this.duracionContrato.meses);
      return options.sort((a, b) => a.meses - b.meses);
    },
    fechaGanada() {
      return formatDateEs(this.oferta.ganadaEl);
    },
    fechaPerdida() {
      return formatDateEs(this.oferta.perdidaEl);
    },
    fechaEntregada() {
      return formatDateEs(this.oferta.entregadaEl);
    },
    fechaVisada() {
      return formatDateEs(this.oferta.visadaEl);
    },
    fechaCaducada() {
      return formatDateEs(this.oferta.caducadaEl);
    },
    fechaCreada() {
      return formatDateEs(this.oferta.creadaEl);
    },
    fechaOferta() {
      return formatDateEs(this.oferta.fecha);
    },
    fechaPrimeraVersion() {
      return formatDateEs(this.ofertaBase.creadoEl);
    },
    fechaFirma() {
      if (!this.oferta.fechaFirmaContrato) {
        return formatDateEs(formatDateAddDaysForm(new Date(), Plazos.DIASPARAFIRMACONTRATO));
      }
      const fechaLimpia = formatDateSinZona(this.oferta.fechaFirmaContrato);
      return formatDateEs(fechaLimpia);
    },
    plan() {
      return this.planes.find((x) => x.id == this.oferta.planId) || {};
    },

    nombreTarifa() {
      return this.tarifa.nombre;
    },
  },
  actions: {
    async cargaInicialDeOferta(id) {
      await this.cargarDatosMaestros();

      if (id > 0) {
        await this.cargarOfertaCompleta(id);
      } else {
        if (this.esPartner) {
          this.oferta.tipoProyecto = TipoProyecto.SAAS;
        }

        // Seteo el primer plan recibido por defecto
        if (this.planes.length > 0) {
          this.oferta.planId = this.planes[0].id;
          this.oferta.numeroUsuarios = 5;
        }

        await this.cargarUsuarioActual();
      }

      this.cargaInicial = false;
    },
    async cargarOfertaCompleta(id) {
      const data = await ofertasApi.cargarCompleta(id);
      this.tieneContrato = await ofertasApi.tieneContrato(id);
      this.ofertaBase = data.ofertaBase;
      this.oferta = data.oferta;
      this.ofertaValoradaEuro = data.ofertaValoradaEuro;
      this.unidadNegocio = data.unidadNegocio;
      this.pais = data.pais;
      this.cliente = data.cliente;
      this.divisa = data.divisa;
      this.modulos = data.modulos;
      this.tarifa = data.tarifa;
      this.ofertaPipedrive = data.ofertaPipedrive;
      this.usuario = data.usuario;
      this.ofertaDescuentoFirma = data.ofertaDescuentoFirma;
      this.duracionContrato = data.duracionContrato;
      this.cliente = data.cliente;
      this.clienteContactos = data.clienteContactos;
      this.motivoCierre = data.motivoCierre;

      this.ofertaModulos = data.ofertaModulos.map((m) => {
        m.eliminado = false;
        m.agregado = false;

        if (this.modulos.hasOwnProperty(m.moduloId)) {
          const modulo = this.modulos[m.moduloId];
          m.permiteCambioPrecio = modulo.permiteCambioPrecio;
          m.permiteCambioHoras = modulo.permiteCambioHoras;
        }

        return m;
      });

      await this.cargarCliente(this.ofertaBase.clienteId);

      const trazabilidadStore = useTrazabilidadStore();
      trazabilidadStore.$patch(data.trazabilidad);
    },
    async cargarUsuarioActual() {
      const { usuario } = await usuariosApi.cargarPerfilActual();

      this.usuario = usuario;
      this.oferta.usuarioId = usuario.id;
    },
    async cargarPlanes() {
      this.planes = await planesApi.listar();
    },
    async cargarCliente(id) {
      const { cliente, contactos } = await clientesApi.cargar(id, false);

      let contacto = contactos.find((c) => c.id == cliente.clienteContactoPorDefectoId);
      if (!contacto) {
        contacto = contactos[0];
      }

      this.clienteContactos = contactos;
      this.cliente = cliente;
      this.oferta.contacto = contacto.contacto;
      this.oferta.cargoContacto = contacto.cargoContacto;
      this.clienteContacto = contacto;
      this.ofertaBase.clienteId = id;
    },
    async cambiarCliente(id) {
      await this.cargarCliente(id);
      this.oferta.codigo = "";
    },
    resetCliente() {
      this.cliente = clientesApi.crear();
      this.clienteContactos = [];
      this.oferta.contacto = "";
      this.oferta.cargoContacto = "";
      this.ofertaBase.clienteId = 0;
      this.oferta.codigo = "";
    },
    async cargarDatosMaestros() {
      // Una vez establecido el cliente, cargamos todos los datos maestros
      await Promise.all([
        this.cargarUnidadesNegocio(),
        this.cargarFormasDePago(),
        this.cargarTiposDescuento(),
        this.cargarComplejidades(),
        this.cargarPlanes(),
        this.cargarUsuarioActual(),
        this.cargarDuracionesContrato(),
      ]);
    },
    async cargarDuracionesContrato() {
      this.duracionesContrato = await duracionesContratoApi.listar();
    },
    async agregarModulo(moduloId) {
      this.validarNuevoModulo(moduloId);

      const oferta = this.oferta;
      const ofertaModulos = this.ofertaModulos;
      const params = { moduloId, oferta, ofertaModulos };

      let { modulo, ofertaModulo } = await ofertasApi.moduloParaEstaOferta(params);

      ofertaModulo.agregado = true;
      ofertaModulo.eliminado = false;

      if (!this.modulos.hasOwnProperty(modulo.id)) {
        this.modulos[modulo.id] = modulo;
      }
      this.ofertaModulos.push(ofertaModulo);
    },
    async calcularValoracionDebounced() {
      if (!this.cargaInicial && this.modulosCorrectos() && this.borrador) {
        const oferta = this.oferta;
        const ofertaModulos = this.ofertaModulos;

        this.oferta = await ofertasApi.calcularValoracion({ oferta, ofertaModulos });
      }
    },
    async cargarUnidadesNegocio() {
      this.unidadesNegocio = await unidadesNegocioApi.autocompletar();
      if (this.unidadesNegocio.length == 1) {
        this.cambiarUnidadNegocio(this.unidadesNegocio[0].id);
      }
    },
    async cambiarUnidadNegocio(id) {
      id = Number(id);

      // Reseteo los datos dependientes de la unidad de negocio
      this.ofertaBase.clienteId = 0;
      this.cliente = clientesApi.crear();
      this.clienteContactos = [];
      this.paises = [];
      this.divisas = [];
      this.oferta.divisaId = 0;
      this.oferta.contacto = "";
      this.oferta.cargoContacto = "";

      // seteo la tarifa de la unidad de negocio
      const tarifa = await unidadesNegocioApi.cargarTarifa(id);
      this.tarifa = tarifa;
      this.oferta.tarifaId = tarifa.id;

      // Cargo los paises de la unidad de negocio
      const unidadNegocioData = await unidadesNegocioApi.cargar(id);
      this.unidadNegocio = unidadNegocioData.unidadNegocio;

      const paisesData = await paisesApi.listar({ unidadNegocioId: id });
      this.paises = paisesData.paises;
      if (this.paises.length == 1) {
        await this.cambiarPais(this.paises[0].id);
      }

      // Asigno la unidad de negocio
      this.ofertaBase.unidadNegocioId = id;
    },
    async cambiarPais(paisId) {
      this.oferta.paisId = paisId;
      this.pais = this.paises.find((p) => p.id == paisId);
      await this.cargarDivisas(paisId);
    },
    async cambiarDivisa(divisaId) {
      this.divisa = this.divisas.find((d) => d.id == divisaId);
      this.oferta.divisaId = this.divisa.id;
    },
    cambiarPlan(id) {
      this.oferta.planId = id;
    },
    cambiarDuracionContrato(id) {
      this.oferta.duracionContratoId = id;
      this.duracionContrato = this.duracionesContrato.find((x) => x.id == id);
    },
    async cargarComplejidades() {
      this.complejidades = await complejidadesApi.listar();
    },
    async cargarDivisas(paisId) {
      this.divisas = await paisesApi.obtenerDivisa(paisId);
      if (this.divisas.length == 1) {
        this.cambiarDivisa(this.divisas[0].id);
      }
    },
    async cargarFormasDePago() {
      this.formasDePago = await formasDePagoApi.listar();
    },
    async cargarTiposDescuento() {
      const dtos = await tipoDescuentoApi.listar();
      this.tiposDescuentoparaCuota = dtos.filter((x) => Boolean(x.paraCuota));
      this.tiposDescuentoParaServicios = dtos.filter((x) => Boolean(x.paraServicios));
    },
    async guardarOfertaCompleta(id) {
      const ofertaCompleta = {
        ofertaBase: this.ofertaBase,
        oferta: this.oferta,
        ofertaModulosAgregados: this.ofertaModulos.filter((m) => m.agregado && !m.eliminado),
        ofertaModulosActualizados: this.ofertaModulos.filter((m) => !m.agregado && !m.eliminado),
        ofertaModulosEliminados: this.ofertaModulos.filter((m) => m.eliminado && !m.agregado),
      };
      if (id) {
        return await ofertasApi.actualizar(ofertaCompleta);
      } else {
        return await ofertasApi.insertar(ofertaCompleta);
      }
    },
    aplicarDescuentos(dtos) {
      this.oferta.tipoDescuentoSuscripcion = dtos.tipoDescuentoSuscripcion;
      this.oferta.tipoDescuentoImplementacion = dtos.tipoDescuentoImplementacion;
      this.oferta.tipoDescuentoCuotaAnual = dtos.tipoDescuentoCuotaAnual;
      this.oferta.tipoDescuentoServicios = dtos.tipoDescuentoServicios;
    },
    yaAgregado(moduloId) {
      return this.ofertaModulos.filter((m) => m.moduloId == moduloId).length > 0;
    },
    validarNuevoModulo(moduloId) {
      if (!moduloId) {
        throw "Debe indicar un módulo";
      }
      if (this.yaAgregado(moduloId)) {
        throw "Ese módulo ya se encuentra en la oferta, aumente la cantidad o añada otro diferente";
      }
      if (!this.oferta.divisaId) {
        throw "Debe seleccionar una divisa";
      }
      if (!this.oferta.paisId) {
        throw "Debe seleccionar un país";
      }
      if (!this.oferta.planId) {
        throw "Debe seleccionar un plan";
      }
      if (!this.oferta.campaniaId) {
        throw "Debe elegir una campaña";
      }
      if (!this.oferta.duracionContratoId) {
        throw "Debe elegir una duración de contrato";
      }
      if (!this.oferta.tipoCuota) {
        throw "Debe elegir una forma de pago de la cuota";
      }
    },
    buildEndpointParaModulos(modulos, tipos) {
      const params = new URLSearchParams({
        divisaId: this.oferta.divisaId,
        planId: this.oferta.planId,
        campaniaId: this.oferta.campaniaId,
        tarifaId: this.oferta.tarifaId,
        modulos: modulos,
        tipos: tipos,
      }).toString();

      return `moduloParaOferta/autocompletar&${params}`;
    },
    comprobarCambiosParmametros(parametros) {
      if (parametros.divisaId != this.oferta.divisaId) {
        return false;
      }
      if (parametros.planId != this.oferta.planId) {
        return false;
      }
      if (parametros.duracionContratoId != this.oferta.duracionContratoId) {
        return false;
      }
      if (parametros.tipoCuota != this.oferta.tipoCuota) {
        return false;
      }
      if (parametros.paisId != this.oferta.paisId) {
        return false;
      }
      if (parametros.tarifaId != this.oferta.tarifaId) {
        return false;
      }
      return true;
    },
    algunCosteCubierto() {
      let tieneCosteCubierto = false;
      for (let i = 0; i < this.ofertaModulos.length; i++) {
        if (!this.ofertaModulos[i].eliminado && this.ofertaModulos[i].cubreCosteDeImplementacion) {
          tieneCosteCubierto = true;
        }
      }

      return tieneCosteCubierto;
    },

    modulosCorrectos() {
      let filasCorrectas = true;
      if (
        this.ofertaModulos != undefined &&
        this.ofertaModulos.length > 0 &&
        this.oferta.divisaId != undefined &&
        this.oferta.planId != undefined
      ) {
        for (let i = 0; i < this.ofertaModulos.length; i++) {
          if (this.ofertaModulos[i].precioSuscripcion === "" || this.ofertaModulos[i].precioSuscripcion < 0) {
            this.ofertaModulos[i].precioSuscripcion = 0;
          }
          if (this.ofertaModulos[i].precioCuotaAnual === "" || this.ofertaModulos[i].precioCuotaAnual < 0) {
            this.ofertaModulos[i].precioCuotaAnual = 0;
          }
          if (this.ofertaModulos[i].numHorasCapacitacion === "" || this.ofertaModulos[i].numHorasCapacitacion < 0) {
            this.ofertaModulos[i].numHorasCapacitacion = 0;
          }
          if (this.ofertaModulos[i].numHorasConsultoria === "" || this.ofertaModulos[i].numHorasConsultoria < 0) {
            this.ofertaModulos[i].numHorasConsultoria = 0;
          }
          if (this.ofertaModulos[i].precioServicio === "" || this.ofertaModulos[i].precioServicio < 0) {
            this.ofertaModulos[i].precioServicio = 0;
          }
        }
      } else {
        filasCorrectas = false;
      }
      return filasCorrectas;
    },
    checkForm() {
      const errores = [];
      if (!this.ofertaBase.clienteId) {
        errores.push("Debe seleccionar un cliente");
      }
      if (!this.ofertaBase.unidadNegocioId) {
        errores.push("Debe seleccionar una unidad de negocio");
      }
      if (this.oferta.descripcion == "") {
        errores.push("Debe indicar una descripción");
      }
      if (!this.oferta.fecha) {
        errores.push("Debe indicar una fecha");
      }
      if (!this.oferta.usuarioId) {
        errores.push("Debe indicar un usuario");
      }
      if (!this.oferta.tarifaId) {
        errores.push("Debe indicar una tarifa");
      }
      if (!this.oferta.tipoProyecto) {
        errores.push("Debe indicar un tipo de proyecto");
      }
      if (!this.oferta.planId) {
        errores.push("Debe indicar un plan");
      }
      if (!this.oferta.campaniaId) {
        errores.push("Debe indicar una campaña");
      }
      if (!this.oferta.duracionContratoId) {
        errores.push("Debe indicar una duración de contrato");
      }
      if (!this.oferta.tipoCuota) {
        errores.push("Debe indicar una forma de pago");
      }
      if (!this.oferta.complejidadProyectoId) {
        errores.push("Debe indicar una complejidad de proyecto");
      }
      if (!this.oferta.formaPagoImplementacionId || !this.oferta.formaPagoSuscripcionId) {
        errores.push("Debe indicar una modalidad de pago");
      }
      if (!this.oferta.origenOfertaId) {
        errores.push("Debe indicar un origen de oferta");
      }

      const fechaOferta = new Date(this.oferta.fecha);
      const fechaFirma = this.oferta.fechaFirmaContrato ? new Date(this.oferta.fechaFirmaContrato) : null;
      const fechaHasta = dayjs().add(Plazos.MAX_DIAS_PARA_FIRMA, "day").toDate();

      if (fechaFirma && fechaFirma < fechaOferta) {
        errores.push("La fecha de firma no puede ser anterior a la fecha de oferta");
      }

      if (fechaFirma && fechaFirma < new Date()) {
        errores.push("La fecha de firma no puede ser anterior a la fecha actual");
      }

      if (fechaFirma && fechaFirma > fechaHasta) {
        errores.push(
          `La fecha de firma no puede ser posterior a ${Plazos.MAX_DIAS_PARA_FIRMA} días desde la fecha actual`
        );
      }

      return errores;
    },
    setOfertaModulos(modulos) {
      this.ofertaModulos = modulos;
    },
  },
});
